import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react';

/**
 * Form validation utilities and components for consistent error handling
 * across all entity forms (Lead, Customer, Opportunity, Activity)
 */

// ============================================
// VALIDATION HELPER FUNCTIONS
// ============================================

/**
 * Check if a value is empty (null, undefined, empty string, or whitespace only)
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const splitEmailList = (email) => {
  if (isEmpty(email)) return [];
  return email
    .split(/[;,]/)
    .map((value) => value.trim())
    .filter(Boolean);
};

export const normalizeEmailList = (email) => {
  if (isEmpty(email)) return '';
  return splitEmailList(email).join(';');
};

/**
 * Validate email format (supports comma/semicolon-separated lists)
 */
export const isValidEmail = (email) => {
  if (isEmpty(email)) return true; // Empty is not invalid format, just missing
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailList = splitEmailList(email);
  if (emailList.length === 0) return true;
  return emailList.every((value) => emailRegex.test(value));
};

/**
 * Validate phone format (flexible international format)
 */
export const isValidPhone = (phone) => {
  if (isEmpty(phone)) return true;
  // Allow digits, spaces, dashes, parentheses, plus sign
  const phoneRegex = /^[\d\s\-\(\)\+\.]{6,20}$/;
  return phoneRegex.test(phone.trim());
};

/**
 * Validate URL format
 */
export const isValidUrl = (url) => {
  if (isEmpty(url)) return true;
  try {
    // Add protocol if missing for validation
    const urlToTest = url.match(/^https?:\/\//) ? url : `https://${url}`;
    new URL(urlToTest);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validate number is positive
 */
export const isPositiveNumber = (value) => {
  if (isEmpty(value)) return true;
  const num = Number(value);
  return !isNaN(num) && num >= 0;
};

/**
 * Validate number is in range
 */
export const isInRange = (value, min, max) => {
  if (isEmpty(value)) return true;
  const num = Number(value);
  return !isNaN(num) && num >= min && num <= max;
};

/**
 * Validate date is not in the past
 */
export const isNotPastDate = (dateStr) => {
  if (isEmpty(dateStr)) return true;
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date >= today;
};

/**
 * Validate date A is after date B
 */
export const isDateAfter = (dateA, dateB) => {
  if (isEmpty(dateA) || isEmpty(dateB)) return true;
  return new Date(dateA) >= new Date(dateB);
};

// ============================================
// BILINGUAL ERROR MESSAGES
// ============================================

export const ValidationMessages = {
  required: {
    th: 'กรุณากรอกข้อมูลนี้',
    en: 'This field is required'
  },
  requiredField: (fieldName) => ({
    th: `กรุณากรอก${fieldName}`,
    en: `Please enter ${fieldName}`
  }),
  invalidEmail: {
    th: 'รูปแบบอีเมลไม่ถูกต้อง',
    en: 'Invalid email format'
  },
  invalidPhone: {
    th: 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง',
    en: 'Invalid phone number format'
  },
  invalidUrl: {
    th: 'รูปแบบ URL ไม่ถูกต้อง',
    en: 'Invalid URL format'
  },
  positiveNumber: {
    th: 'ตัวเลขต้องมากกว่าหรือเท่ากับ 0',
    en: 'Number must be positive or zero'
  },
  numberRange: (min, max) => ({
    th: `ตัวเลขต้องอยู่ระหว่าง ${min} ถึง ${max}`,
    en: `Number must be between ${min} and ${max}`
  }),
  pastDate: {
    th: 'วันที่ต้องไม่เป็นอดีต',
    en: 'Date cannot be in the past'
  },
  dateOrder: (firstField, secondField) => ({
    th: `${firstField} ต้องอยู่ก่อน ${secondField}`,
    en: `${firstField} must be before ${secondField}`
  }),
  contactOrCompanyRequired: {
    th: 'กรุณากรอกชื่อผู้ติดต่อ หรือ ชื่อบริษัท อย่างน้อยหนึ่งอย่าง',
    en: 'Please enter at least Contact Name or Company Name'
  },
  titleRequired: {
    th: 'กรุณากรอกหัวข้อ',
    en: 'Please enter a title'
  },
  subjectRequired: {
    th: 'กรุณากรอกเรื่อง',
    en: 'Please enter a subject'
  },
  activityTypeRequired: {
    th: 'กรุณาเลือกประเภทกิจกรรม',
    en: 'Please select an activity type'
  },
  customerTypeRequired: {
    th: 'กรุณาเลือกประเภทลูกค้า',
    en: 'Please select customer type'
  },
  firstNameRequired: {
    th: 'กรุณากรอกชื่อ',
    en: 'Please enter first name'
  },
  lastNameRequired: {
    th: 'กรุณากรอกนามสกุล',
    en: 'Please enter last name'
  },
  companyNameRequired: {
    th: 'กรุณากรอกชื่อบริษัท',
    en: 'Please enter company name'
  },
  amountPositive: {
    th: 'จำนวนเงินต้องมากกว่า 0',
    en: 'Amount must be greater than 0'
  }
};

/**
 * Get bilingual message string
 */
export const getBilingualMessage = (messageObj) => {
  if (typeof messageObj === 'string') return messageObj;
  if (!messageObj) return '';
  return `${messageObj.th} / ${messageObj.en}`;
};

// ============================================
// VALIDATION SUMMARY COMPONENT
// ============================================

/**
 * ValidationSummary - Displays a summary of all validation errors at the top of the form
 */
export const ValidationSummary = ({ errors, onFieldClick }) => {
  const errorList = Object.entries(errors).filter(([_, value]) => value);
  
  if (errorList.length === 0) return null;
  
  return (
    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg animate-shake">
      <div className="flex items-start space-x-3">
        <XCircle className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-2">
            ไม่สามารถบันทึกได้ / Cannot Save
          </h4>
          <p className="text-xs text-red-700 dark:text-red-400 mb-3">
            กรุณาแก้ไขข้อผิดพลาดด้านล่าง / Please fix the following errors:
          </p>
          <ul className="space-y-1">
            {errorList.map(([field, message]) => (
              <li 
                key={field}
                className="text-sm text-red-700 dark:text-red-400 flex items-center space-x-2 cursor-pointer hover:text-red-900 dark:hover:text-red-300 transition-colors"
                onClick={() => onFieldClick?.(field)}
              >
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{typeof message === 'object' ? getBilingualMessage(message) : message}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ============================================
// FIELD ERROR COMPONENT
// ============================================

/**
 * FieldError - Displays error message below a form field
 */
export const FieldError = ({ error, className = '' }) => {
  if (!error) return null;
  
  const message = typeof error === 'object' ? getBilingualMessage(error) : error;
  
  return (
    <div className={`mt-1.5 flex items-center space-x-1.5 text-red-600 dark:text-red-400 ${className}`}>
      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
      <span className="text-xs font-medium">{message}</span>
    </div>
  );
};

// ============================================
// FIELD WRAPPER WITH ERROR STYLING
// ============================================

/**
 * Get input field classes based on error state
 */
export const getInputClassName = (hasError, baseClass = '') => {
  const base = baseClass || 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none transition-colors dark:bg-gray-700 dark:text-white';
  
  if (hasError) {
    return `${base} border-red-400 dark:border-red-500 focus:border-red-500 focus:ring-red-200 dark:focus:ring-red-800 bg-red-50 dark:bg-red-900/10`;
  }
  
  return `${base} border-gray-300 dark:border-gray-600 focus:border-teal-500 focus:ring-teal-200 dark:focus:ring-teal-800`;
};

/**
 * Get select field classes based on error state
 */
export const getSelectClassName = (hasError, baseClass = '') => {
  return getInputClassName(hasError, baseClass);
};

/**
 * Get textarea field classes based on error state
 */
export const getTextareaClassName = (hasError, baseClass = '') => {
  return getInputClassName(hasError, baseClass);
};

// ============================================
// REQUIRED FIELD LABEL COMPONENT
// ============================================

/**
 * FormLabel - Label with optional required indicator
 */
export const FormLabel = ({ children, required = false, htmlFor, className = '' }) => {
  return (
    <label 
      htmlFor={htmlFor}
      className={`block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 ${className}`}
    >
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
};

// ============================================
// SUCCESS MESSAGE COMPONENT
// ============================================

/**
 * SuccessMessage - Displays success message after form submission
 */
export const SuccessMessage = ({ message, onClose }) => {
  if (!message) return null;
  
  return (
    <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
          <span className="text-sm text-green-800 dark:text-green-300">{message}</span>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
          >
            <XCircle className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================
// WARNING MESSAGE COMPONENT
// ============================================

/**
 * WarningMessage - Displays warning message
 */
export const WarningMessage = ({ message, details, onClose }) => {
  if (!message) return null;
  
  return (
    <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-sm font-medium text-amber-800 dark:text-amber-300">{message}</span>
            {details && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{details}</p>
            )}
          </div>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300"
          >
            <XCircle className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================
// INFO TIP COMPONENT
// ============================================

/**
 * InfoTip - Small info tooltip for field hints
 */
export const InfoTip = ({ message }) => {
  if (!message) return null;
  
  return (
    <div className="relative group inline-block ml-1">
      <Info className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help" />
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
        {message}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700"></div>
      </div>
    </div>
  );
};

// ============================================
// SCROLL TO ERROR FIELD
// ============================================

/**
 * Scroll to the first field with an error
 */
export const scrollToFirstError = (errors) => {
  const firstErrorField = Object.keys(errors).find(key => errors[key]);
  if (firstErrorField) {
    const element = document.getElementById(firstErrorField) || 
                   document.querySelector(`[name="${firstErrorField}"]`) ||
                   document.querySelector(`[data-field="${firstErrorField}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus?.();
    }
  }
};

// ============================================
// CSS FOR SHAKE ANIMATION (add to global styles)
// ============================================

// Add this CSS to your global styles or tailwind config:
// @keyframes shake {
//   0%, 100% { transform: translateX(0); }
//   10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
//   20%, 40%, 60%, 80% { transform: translateX(4px); }
// }
// .animate-shake { animation: shake 0.5s ease-in-out; }

export default {
  isEmpty,
  isValidEmail,
  normalizeEmailList,
  isValidPhone,
  isValidUrl,
  isPositiveNumber,
  isInRange,
  isNotPastDate,
  isDateAfter,
  ValidationMessages,
  getBilingualMessage,
  ValidationSummary,
  FieldError,
  FormLabel,
  SuccessMessage,
  WarningMessage,
  InfoTip,
  getInputClassName,
  getSelectClassName,
  getTextareaClassName,
  scrollToFirstError
};

